/**
 * Banner over the crawl-intelligence dashboard naming WHAT the rows in the selected window
 * contain, from the backend's `meta.capture_scope_notes` on /crawl-intelligence/summary
 * (task-fm66ezuxve, site_capture_scope_notes). Example: since 2026-07-11 every TradeInn store's
 * traffic is written under bikeinn's site_id, so bikeinn's bot and traffic series are the whole
 * estate; the forward fix will cut them to a fifth from one day to the next, and this is the only
 * place a reader is told why.
 *
 * The words are the backend's: each note's `note` and `scope` are rendered verbatim, and the dates
 * are the calendar days the row names ('YYYY-MM-DD' text, never re-parsed). The admin adds
 * structure only. Three states, none of them silent:
 *   - meta undefined: a backend older than this row -> nothing rendered (nothing to claim);
 *   - meta.available false: the table could not be read on that box -> the backend's reason,
 *     shown, never folded into "no notes";
 *   - notes: one line per note, an open note (to_date null) says so.
 */
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Info } from 'lucide-react';

export interface CaptureScopeNote {
  id: number;
  from_date: string;
  to_date: string | null;   // null = open-ended
  scope: string;
  note: string;
}

export interface CaptureScopeNotesMeta {
  available: boolean;
  reason: string | null;
  window: { from_date: string; to_date: string } | null;
  notes: CaptureScopeNote[];
}

export function CaptureScopeNotesBanner({ meta }: { meta: CaptureScopeNotesMeta | undefined }) {
  if (!meta) return null;

  if (!meta.available) {
    return (
      <Alert data-testid="capture-scope-notes-unavailable">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Capture scope notes could not be read</AlertTitle>
        <AlertDescription>{meta.reason}</AlertDescription>
      </Alert>
    );
  }

  if (meta.notes.length === 0) return null;

  const windowLabel = meta.window ? ` (${meta.window.from_date} → ${meta.window.to_date})` : '';
  return (
    <Alert data-testid="capture-scope-notes">
      <Info className="h-4 w-4" />
      <AlertTitle>What the rows in this window contain{windowLabel}</AlertTitle>
      <AlertDescription>
        <ul className="mt-1 space-y-1">
          {meta.notes.map((n) => (
            <li key={n.id} className="flex flex-wrap items-baseline gap-2" data-testid="capture-scope-note">
              <span className="font-mono text-xs whitespace-nowrap">
                {n.from_date} → {n.to_date ?? 'open'}
              </span>
              <Badge variant="outline">{n.scope}</Badge>
              <span>{n.note}</span>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
